import React, { useEffect, useState } from "react";
import { getAlbums, getGalleryItems } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { Image, Film, X, ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";

export default function GalleryPage() {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    getAlbums().then(r => setAlbums(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch

  useEffect(() => {
    if (selectedAlbum) {
      getGalleryItems(selectedAlbum.id).then(r => setItems(r.data)).catch(() => {});
    }
  }, [selectedAlbum]);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const nextImg = () => setLightbox(prev => (prev + 1) % items.length);
  const prevImg = () => setLightbox(prev => (prev - 1 + items.length) % items.length);

  return (
    <div data-testid="gallery-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <div className="bg-gradient-to-br from-[#1670B9] via-[#2d8fd4] to-[#74CDD9] pt-32 pb-20 text-center">
        <h1 className="font-['Quicksand'] text-4xl sm:text-5xl font-light text-white mb-4" data-testid="gallery-title">Galerie</h1>
        <p className="text-white/80 text-base max-w-xl mx-auto">Photos et vidéos de nos activités scientifiques</p>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {!selectedAlbum ? (
          <>
            {albums.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-xl font-['Quicksand']">Aucun album pour le moment</p>
                <p className="text-sm mt-2">Les albums seront ajoutés via le panneau d'administration.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {albums.map((album, i) => (
                  <motion.div key={album.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    onClick={() => setSelectedAlbum(album)} className="group cursor-pointer bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all" data-testid={`album-${i}`}>
                    <div className="h-48 overflow-hidden bg-slate-100">
                      {album.cover_url ? (
                        <img src={album.cover_url} alt={album.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Image className="w-12 h-12 text-slate-300" /></div>
                      )}
                    </div>
                    <div className="p-4">
                      <span className="text-xs font-semibold text-[#1670B9] uppercase tracking-wider">{album.category}</span>
                      <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mt-1">{album.title}</h3>
                      {album.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{album.description}</p>}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => { setSelectedAlbum(null); setItems([]); }} className="inline-flex items-center gap-2 text-sm text-[#1670B9] font-medium mb-6 hover:underline" data-testid="back-to-albums">
              <ChevronLeft className="w-4 h-4" /> Retour aux albums
            </button>
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-2">{selectedAlbum.title}</h2>
            {selectedAlbum.description && <p className="text-sm text-slate-500 mb-8">{selectedAlbum.description}</p>}

            {items.length === 0 ? (
              <div className="text-center py-16 text-slate-400"><p>Cet album est vide.</p></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map((item, i) => (
                  <motion.div key={item.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
                    onClick={() => item.media_type === "image" && openLightbox(i)}
                    className="group cursor-pointer rounded-xl overflow-hidden bg-white border border-slate-100 hover:shadow-sm transition-shadow" data-testid={`gallery-item-${i}`}>
                    {item.media_type === "video" ? (
                      <div className="relative h-40">
                        <video src={item.url} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20"><Film className="w-8 h-8 text-white" /></div>
                      </div>
                    ) : (
                      <img src={item.url} alt={item.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    )}
                    {item.title && <p className="text-xs text-slate-500 p-2 truncate">{item.title}</p>}
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
            <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"><X className="w-6 h-6" /></button>
            <button onClick={(e) => { e.stopPropagation(); prevImg(); }} className="absolute left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={(e) => { e.stopPropagation(); nextImg(); }} className="absolute right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronRight className="w-6 h-6" /></button>
            <img src={items[lightbox].url} alt="" className="max-w-[90vw] max-h-[85vh] rounded-xl object-contain" onClick={e => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
